import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageSquare, 
  Send, 
  ArrowLeft,
  Lock,
  Users,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [myProfileId, setMyProfileId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
    }
  }, [activeConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      
      // Get my profile ID
      const profileRes = await axios.get(`${API}/community/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProfileId(profileRes.data.id);
      
      const response = await axios.get(`${API}/community/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
      
      // Check if we should open a specific conversation
      const toProfile = searchParams.get('to');
      if (toProfile) {
        // Check if conversation exists
        const existingConv = response.data.conversations?.find(c => 
          c.participant_ids.includes(toProfile)
        );
        if (existingConv) {
          setActiveConversation(existingConv);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No profile yet
        navigate('/community/onboarding');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/community/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar mensajes' : 'Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const toProfileId = activeConversation.participant_ids.find(id => id !== myProfileId);
    if (!toProfileId) return;

    setSending(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/messages`, {
        to_profile_id: toProfileId,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages(activeConversation.id);
      fetchConversations(); // Refresh conversation list
    } catch (error) {
      toast.error(language === 'es' ? 'Error al enviar' : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="messages-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-3 gap-4 h-[600px]">
          <Skeleton className="h-full" />
          <Skeleton className="h-full md:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/community')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            {language === 'es' ? 'Mensajes' : 'Messages'}
            <Lock className="h-5 w-5 text-success" />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {language === 'es' ? 'Mensajes encriptados' : 'Encrypted messages'}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 h-[600px]">
        {/* Conversations List */}
        <Card className="md:col-span-1 overflow-hidden">
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <h3 className="font-semibold">
                {language === 'es' ? 'Conversaciones' : 'Conversations'}
              </h3>
            </div>
            <ScrollArea className="h-[520px]">
              {conversations.length > 0 ? (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                      activeConversation?.id === conv.id ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => setActiveConversation(conv)}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">
                          {conv.participant?.display_name || (language === 'es' ? 'Defensor' : 'Advocate')}
                        </h4>
                        <p className="text-sm text-muted-foreground truncate">
                          {conv.last_message_preview || (language === 'es' ? 'Sin mensajes' : 'No messages')}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-primary">{conv.unread_count}</Badge>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'Sin conversaciones' : 'No conversations'}</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="md:col-span-2 overflow-hidden flex flex-col">
          {activeConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">
                    {activeConversation.participant?.display_name || (language === 'es' ? 'Defensor' : 'Advocate')}
                  </h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    {language === 'es' ? 'Encriptado' : 'Encrypted'}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.from_profile_id === myProfileId ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${index}`}
                    >
                      <div
                        className={`max-w-[70%] p-3 rounded-lg ${
                          msg.from_profile_id === myProfileId
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.from_profile_id === myProfileId ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder={language === 'es' ? 'Escribe un mensaje...' : 'Type a message...'}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-btn">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>{language === 'es' ? 'Selecciona una conversación' : 'Select a conversation'}</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Messages;
